/**
 * PreviewHeader - Unified header component for preview windows and overlays
 *
 * Works in two contexts:
 * - Electron windows: badges on the left, actions pinned to the right
 * - Viewer overlays: badges on the left, actions pinned to the right
 *
 * Use `onClose` prop to show the close button on the right.
 */

import * as React from 'react'
import { X, type LucideIcon } from 'lucide-react'
import { useTranslation } from '@okclawpro/i18n/react'
import { cn } from '../../lib/utils'

/**
 * Badge variants using semantic colors
 */
export const PREVIEW_BADGE_VARIANTS = {
  edit: 'text-foreground/70',
  write: 'text-foreground/70',
  read: 'text-foreground/70',
  bash: 'text-foreground/70',
  grep: 'text-foreground/70',
  glob: 'text-foreground/70',
  blue: 'text-foreground/70',
  amber: 'text-foreground/70',
  orange: 'text-foreground/70',
  green: 'text-foreground/70',
  purple: 'text-foreground/70',
  gray: 'text-foreground/70',
  default: 'text-foreground/70',
} as const

export type PreviewBadgeVariant = keyof typeof PREVIEW_BADGE_VARIANTS

/**
 * Shared surface geometry for every preview-header control.
 * Keep badges and file actions on the same visual baseline so they cannot
 * drift when one side of the toolbar is updated independently.
 */
export const PREVIEW_HEADER_CONTROL_CLASSES =
  'inline-flex appearance-none border-0 m-0 items-center gap-1.5 h-[28px] px-2 rounded-[6px] bg-background font-sans text-[12px] font-medium text-foreground/70 shadow-minimal'

export const PREVIEW_HEADER_CONTROL_INTERACTION_CLASSES =
  'transition-colors hover:bg-foreground/[0.05] hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring'

export interface PreviewHeaderBadgeProps {
  /** Icon component to display */
  icon?: LucideIcon
  /** Badge label text */
  label: string
  /** Badge variant (default: 'default') */
  variant?: PreviewBadgeVariant
  /** Click handler (makes it a clickable link-style button) */
  onClick?: () => void
  /** Title for tooltip */
  title?: string
  /** Additional className */
  className?: string
  /** Allow badge to shrink (for long paths) - default: false */
  shrinkable?: boolean
  /** Render as a button in toolbars where all controls share button geometry. */
  asButton?: boolean
  /** Inline layout overrides for badges that participate in a flexible header */
  style?: React.CSSProperties
}

/**
 * PreviewHeaderBadge - Badge component for preview headers
 *
 * Style specs:
 * - Height: 28px
 * - Padding: 8px horizontal
 * - Border radius: 6px
 * - Font: Sans-serif, 12px, medium weight
 * - Truncation: CSS truncate, shrink, stay 1 line
 * - Clickable: underline on hover, pointer cursor
 */
export function PreviewHeaderBadge({
  icon: Icon,
  label,
  variant = 'default',
  onClick,
  title,
  className,
  shrinkable = false,
  asButton = false,
  style,
}: PreviewHeaderBadgeProps) {
  const variantClasses = PREVIEW_BADGE_VARIANTS[variant]
  const baseClasses = cn(
    PREVIEW_HEADER_CONTROL_CLASSES,
    variantClasses,
    className
  )

  if (onClick || asButton) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          baseClasses,
          'preview-file-action group',
          shrinkable ? 'min-w-0' : 'shrink-0',
          PREVIEW_HEADER_CONTROL_INTERACTION_CLASSES,
          onClick ? 'cursor-pointer' : 'cursor-default'
        )}
        title={title || label}
        tabIndex={onClick ? undefined : -1}
        aria-disabled={onClick ? undefined : true}
        style={style}
      >
        {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
        <span className={cn('truncate', onClick && 'group-hover:underline')}>{label}</span>
      </button>
    )
  }

  return (
    <div className={cn(baseClasses, shrinkable ? 'min-w-0' : 'shrink-0')} title={title || label} style={style}>
      {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
      <span className="truncate">{label}</span>
    </div>
  )
}

export interface PreviewHeaderProps {
  /** Badge elements to render in center */
  children?: React.ReactNode
  /** Close handler - when provided, shows X button on right */
  onClose?: () => void
  /** Actions to render on the right, just before the close button */
  rightActions?: React.ReactNode
  /** Height of the header (default: 50px for windows, 44px for overlays) */
  height?: number
  /** Additional className for the header */
  className?: string
  /** Inline styles */
  style?: React.CSSProperties
}

/**
 * PreviewHeader - Header/toolbar for preview windows and overlays
 *
 * Layout:
 * - Left: type/status/path badges
 * - Right: file actions and close button
 */
export function PreviewHeader({
  children,
  onClose,
  rightActions,
  height = 50,
  className,
  style,
}: PreviewHeaderProps) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'preview-header w-full shrink-0 self-stretch flex flex-nowrap items-center px-3',
        className
      )}
      style={{
        height,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        ...style,
      }}
    >
      {/* Left side - space for traffic lights on macOS, flex-1 to balance with right side */}
      <div
        className="preview-header-spacer"
        style={{ flex: '0 0 0px', minWidth: 0, width: 0 }}
      />

      {/* Center - badges row. no-drag so badges are clickable in the window drag region. */}
      <div
        className="preview-header-badges flex items-center gap-2"
        style={{
          WebkitAppRegion: 'no-drag',
          flex: '1 1 auto',
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
        } as React.CSSProperties}
      >
        {children}
      </div>

      {/* Right side - actions + close button. no-drag so actions are clickable in the window drag region. */}
      <div
        className="preview-header-actions flex shrink-0 items-center gap-2 justify-end"
        style={{
          WebkitAppRegion: 'no-drag',
          flex: '0 0 auto',
          minWidth: 'max-content',
          marginLeft: 'auto',
        } as React.CSSProperties}
      >
        {rightActions}
        {onClose && (
          <button
            onClick={onClose}
            className={cn(
              'p-1.5 rounded-[6px] bg-background shadow-minimal cursor-pointer',
              'opacity-70 hover:opacity-100 transition-opacity',
              'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring'
            )}
            title={t('overlay.close')}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}
